import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const AdminDashboardSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SkeletonTheme baseColor="#f0f0f0" highlightColor="#e0e0e0">
      <Box>
        <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
          <Skeleton height={isMobile ? 28 : 40} width={isMobile ? 150 : 200} style={{ marginBottom: 8, borderRadius: 8 }} />
          <Skeleton height={isMobile ? 14 : 20} width={isMobile ? '100%' : 400} style={{ borderRadius: 6 }} />
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
          {[...Array(4)].map((_, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', borderRadius: { xs: '16px', sm: '20px' }, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Skeleton
                      width={isMobile ? 50 : 60}
                      height={isMobile ? 50 : 60}
                      style={{ borderRadius: isMobile ? 14 : 16 }}
                    />
                  </Box>
                  <Skeleton height={isMobile ? 24 : 36} width={isMobile ? '70%' : '60%'} style={{ marginBottom: 4, borderRadius: 6 }} />
                  <Skeleton height={isMobile ? 12 : 16} width={isMobile ? '90%' : '80%'} style={{ borderRadius: 4 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, height: '100%', borderRadius: { xs: '16px', sm: '20px' }, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Skeleton height={isMobile ? 20 : 24} width={isMobile ? 100 : 150} />
                <Skeleton height={isMobile ? 28 : 32} width={isMobile ? 60 : 80} />
              </Box>

              <Box
                sx={{
                  height: { xs: 200, md: 300 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <Skeleton height="100%" width="100%" />
              </Box>

              <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={1}>
                {[...Array(3)].map((_, index) => (
                  <Box textAlign="center" key={index} sx={{ minWidth: { xs: 60, sm: 80 } }}>
                    <Skeleton height={isMobile ? 12 : 16} width={isMobile ? 60 : 80} style={{ marginBottom: 4 }} />
                    <Skeleton height={isMobile ? 18 : 24} width={isMobile ? 40 : 60} />
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, height: '100%', borderRadius: { xs: '16px', sm: '20px' }, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
              <Skeleton height={isMobile ? 20 : 24} width={isMobile ? 120 : 150} style={{ marginBottom: 16 }} />

              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Skeleton height={16} width="50%" />
                      <Skeleton height={16} width={50} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><Skeleton height={16} width={60} /></TableCell>
                        <TableCell align="right"><Skeleton height={16} width={40} /></TableCell>
                        <TableCell align="right"><Skeleton height={16} width={60} /></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...Array(5)].map((_, index) => (
                        <TableRow key={index}>
                          <TableCell><Skeleton height={16} width={120} /></TableCell>
                          <TableCell align="right"><Skeleton height={16} width={30} /></TableCell>
                          <TableCell align="right"><Skeleton height={16} width={50} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Skeleton height={32} width={100} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: '16px', sm: '20px' }, boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Skeleton height={isMobile ? 20 : 24} width={isMobile ? 120 : 150} />
            <Skeleton height={isMobile ? 28 : 32} width={isMobile ? 60 : 80} />
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[...Array(5)].map((_, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Skeleton height={16} width={80} />
                    <Skeleton height={20} width={60} style={{ borderRadius: 8 }} />
                  </Box>
                  <Skeleton height={14} width="70%" style={{ marginBottom: 4 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Skeleton height={14} width={60} />
                    <Skeleton height={28} width={50} />
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><Skeleton height={16} width={80} /></TableCell>
                    <TableCell><Skeleton height={16} width={80} /></TableCell>
                    {!isTablet && <TableCell><Skeleton height={16} width={60} /></TableCell>}
                    <TableCell><Skeleton height={16} width={60} /></TableCell>
                    <TableCell><Skeleton height={16} width={60} /></TableCell>
                    {!isTablet && <TableCell><Skeleton height={16} width={60} /></TableCell>}
                    <TableCell align="right"><Skeleton height={16} width={60} /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton height={16} width={100} /></TableCell>
                      <TableCell><Skeleton height={16} width={120} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={16} width={150} /></TableCell>}
                      <TableCell><Skeleton height={16} width={70} /></TableCell>
                      <TableCell><Skeleton height={24} width={80} /></TableCell>
                      {!isTablet && <TableCell><Skeleton height={16} width={100} /></TableCell>}
                      <TableCell align="right"><Skeleton height={32} width={60} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </SkeletonTheme>
  );
};

export default AdminDashboardSkeleton;
